import type {EventSocketMessage, ShopLevelServices, AccountLevelServices} from '@myparcel-frontend/event-service';
import type {EnvironmentTypes} from '@myparcel-frontend/service-utils';
import type {Ref, WatchHandle} from 'vue';
import type {ComposerTranslation} from 'vue-i18n';
import {useEventService, EventService} from '@myparcel-frontend/event-service';
import {Feature} from '@myparcel-frontend/iam-service';
import {reactive, watch} from 'vue';
import useEventServiceEvents from './events/useEventServiceEvents';
import useOrderServiceEvents from './events/useOrderServiceEvents';
import usePrintingServiceEvents from './events/usePrintingServiceEvents';
import useProductServiceEvents from './events/useProductServiceEvents';
import useFeatures from './useFeatures';
import {useServiceAccount} from './useServiceAccount/useServiceAccount';
import useShop from './useShop/useShop';
import logToMonitor from '@/utils/logging';

type EventServiceName = (typeof EventService)[keyof typeof EventService];

type ConnectionOptions = {
  heartBeat: boolean;
  environment?: EnvironmentTypes;
};

const isShopLevelService = (service: EventServiceName): service is ShopLevelServices =>
  [EventService.Order].includes(service as ShopLevelServices);

const allShopIds = reactive({} as Record<EventServiceName, Set<string>>);
// Store established websocket data refs per service (not reactive itself)
const allWebSocketDataRefs: Partial<Record<EventServiceName, Ref<string | null, string | null>>> = {};

// Persisted access token for websocket connections. Made optional so tests can reset state.
let savedToken: string | undefined;
let watchHandle: WatchHandle | undefined = undefined;
let translations: ComposerTranslation;

export default function useWebsockets(options: ConnectionOptions = {heartBeat: true}) {
  const {isFeatureActive} = useFeatures();
  const {isIamFeatureActive, isSalesChannelsAccessible} = useServiceAccount();
  const {currentShop} = useShop();
  const {handleOrderMessage} = useOrderServiceEvents();
  const {handleEventMessage} = useEventServiceEvents();
  const {handleProductMessage} = useProductServiceEvents();
  const {handlePrintingMessage} = usePrintingServiceEvents();

  const allAvailableServicesAndHandlers: Record<EventServiceName, ((message: EventSocketMessage) => void) | undefined> =
    {
      [EventService.Event]: (message) => {
        handleEventMessage(message, translations);
      },
      [EventService.Order]: handleOrderMessage,
      [EventService.Product]: handleProductMessage,
      [EventService.Printing]: (message) => {
        handlePrintingMessage(message, translations);
      },
    };

  const isActiveService = (serviceName: EventServiceName): boolean => {
    const serviceFeatures: Record<EventServiceName, boolean> = {
      [EventService.Event]: isSalesChannelsAccessible.value,
      [EventService.Order]: isIamFeatureActive(Feature.ORDER_MANAGEMENT),
      [EventService.Product]: isIamFeatureActive(Feature.ORDER_MANAGEMENT),
      [EventService.Printing]: isIamFeatureActive(Feature.DIRECT_PRINTING),
    };
    return serviceFeatures[serviceName];
  };

  const connect = (serviceName: EventServiceName) => {
    if (!savedToken) {
      logToMonitor(new Error('No access token provided for WebSocket connection: ' + serviceName), {
        _myparcel_extra_info: 'Failed to initiate websocket connection because token missing',
        _myparcel_websocket_service: serviceName,
        _myparcel_shop_ids: Array.from(allShopIds[serviceName] ?? []),
      });
      return;
    }

    if (!isActiveService(serviceName)) {
      return;
    }

    if (!allAvailableServicesAndHandlers[serviceName]) {
      return;
    }

    let data: Ref<string | null, string | null>;

    if (isShopLevelService(serviceName)) {
      if (!allShopIds[serviceName]) {
        allShopIds[serviceName] = new Set([currentShop.value.id.toString()]);
      }
      const shopIdArray = Array.from(allShopIds[serviceName]);

      data = useEventService({
        accessToken: savedToken,
        environment: options.environment,
        serviceName,
        shopIds: shopIdArray,
        withLogging: isFeatureActive('debug.websockets'),
        heartBeat: options.heartBeat,
      }).data;
    } else {
      data = useEventService({
        accessToken: savedToken,
        environment: options.environment,
        serviceName: serviceName as AccountLevelServices,
        withLogging: isFeatureActive('debug.websockets'),
        heartBeat: options.heartBeat,
      }).data;
    }

    watch(data, (newValue: string | null) => {
      if (!newValue) return;

      let parsedMessage: EventSocketMessage;

      try {
        parsedMessage = JSON.parse(newValue) as EventSocketMessage;
      } catch (error) {
        logToMonitor(error as Error, {
          _myparcel_extra_info: 'Failed to parse websocket message in useWebsockets composable',
          _myparcel_value_to_change: newValue,
        });
        return;
      }

      const handler = allAvailableServicesAndHandlers[serviceName];
      /* istanbul ignore next */
      if (handler) handler(parsedMessage);
    });

    if (!allWebSocketDataRefs[serviceName]) {
      allWebSocketDataRefs[serviceName] = data;
    }

    return data;
  };

  const setShopIds = (serviceName: EventServiceName, shopIds: string[]) => {
    allShopIds[serviceName] = new Set(shopIds);
    // reconnect with new shop ids
    connect(serviceName);
  };

  const init = (token: string, t: ComposerTranslation) => {
    savedToken = token;
    translations = t;

    // Connect to all services
    Object.values(EventService).forEach((serviceName) => {
      connect(serviceName);
    });

    if (watchHandle) {
      watchHandle.stop();
    }

    // Watch for changes in the current shop and update the shop IDs accordingly
    watchHandle = watch(
      () => currentShop.value.id,
      (newValue) => {
        if (newValue) {
          Object.values(EventService)
            .filter(isShopLevelService)
            .forEach((serviceName) => {
              allShopIds[serviceName] = new Set([newValue.toString()]);
              connect(serviceName);
            });
        }
      },
    );
  };

  return {
    init,
    connect,
    setShopIds,
    /** Test-only helper to reset internal module state between specs. */
    __resetWebsocketsForTest: () => {
      savedToken = undefined;
      Object.keys(allShopIds).forEach((k) => delete (allShopIds as Record<string, unknown>)[k]);
      Object.keys(allWebSocketDataRefs).forEach((k) => delete (allWebSocketDataRefs as Record<string, unknown>)[k]);
      if (watchHandle) {
        watchHandle.stop();
        watchHandle = undefined;
      }
    },
  };
}
